// Google Docs content extraction using public export URLs

import type { DocsExtractionResult } from './types';
import { GoogleExtractionError } from './errors';

/**
 * Default timeout for Google Docs export requests (10 seconds)
 */
const DOCS_EXPORT_TIMEOUT = 10000;

/**
 * Builds the Google Docs plain text export URL
 *
 * @param docId - The Google Docs document ID
 * @returns The export URL for plain text format
 */
export function buildDocsExportUrl(docId: string): string {
  return `https://docs.google.com/document/d/${docId}/export?format=txt`;
}

/**
 * Cleans and normalizes text content from Google Docs export
 *
 * - Converts Windows line endings (CRLF) to Unix (LF)
 * - Reduces multiple consecutive newlines to maximum of 2
 * - Trims leading and trailing whitespace
 *
 * @param text - Raw text from Google Docs export
 * @returns Normalized text content
 */
function normalizeTextContent(text: string): string {
  return text
    .replace(/\r\n/g, '\n') // Convert Windows line endings
    .replace(/\n{3,}/g, '\n\n') // Reduce multiple newlines to max 2
    .trim();
}

/**
 * Extracts the document title from the first line of content
 *
 * @param content - The normalized document content
 * @returns The document title or default
 */
function extractTitle(content: string): string {
  if (!content) {
    return 'Google Document';
  }

  const firstLine = content.split('\n')[0]?.trim();
  return firstLine || 'Google Document';
}

/**
 * Extracts content from a public Google Docs document
 *
 * Uses Google's public export URL which works for documents
 * with "Anyone with the link" sharing enabled.
 *
 * No API key required for public documents.
 *
 * @param docId - The Google Docs document ID
 * @returns Extraction result with text content
 * @throws GoogleExtractionError for access, network, or extraction errors
 */
export async function extractGoogleDocs(docId: string): Promise<DocsExtractionResult> {
  const exportUrl = buildDocsExportUrl(docId);

  try {
    const response = await fetch(exportUrl, {
      headers: {
        Accept: 'text/plain',
      },
      signal: AbortSignal.timeout(DOCS_EXPORT_TIMEOUT),
    });

    // Handle HTTP errors
    if (!response.ok) {
      if (response.status === 403 || response.status === 401) {
        throw new GoogleExtractionError('PRIVATE_DOCUMENT');
      }

      if (response.status === 404) {
        throw new GoogleExtractionError('NOT_FOUND');
      }

      if (response.status === 429) {
        throw new GoogleExtractionError('RATE_LIMITED');
      }

      throw new GoogleExtractionError('EXTRACTION_FAILED');
    }

    const rawText = await response.text();
    const content = normalizeTextContent(rawText);
    const title = extractTitle(content);

    return {
      title,
      content,
      characterCount: content.length,
    };
  } catch (error) {
    // Re-throw GoogleExtractionError as-is
    if (error instanceof GoogleExtractionError) {
      throw error;
    }

    // Handle timeout/abort errors
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new GoogleExtractionError('TIMEOUT');
    }

    // Handle other errors
    throw new GoogleExtractionError('EXTRACTION_FAILED');
  }
}
